// Attaches the session credential to every /api/ request. First, so that no data fetch
// can be issued before it is in place.
import "./utils/installApiAuthHeaders";
import "./utils/installFetchResponseBodyMeta";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { applyDesignSystemPreview } from "./theme/mmecDsPreview";
import App from "./App";
import { patchGlobalFetchForDesktop } from "./services/desktopApi";

// ── Desktop encrypted tunnel: intercept API fetch() before any data loads ──
patchGlobalFetchForDesktop();
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";
import { DataModeProvider } from "./contexts/DataModeContext";
import { AdvancedAnalysisProvider } from "./contexts/AdvancedAnalysisContext";
import { SymbolMappingProvider } from "./contexts/SymbolMappingContext";
import { DateFilterProvider } from "./contexts/DateFilterContext";
import { TradingCallProvider } from "./contexts/TradingCallContext";
import { DataSourceProvider } from "./contexts/DataSourceContext";
import { WatchlistProvider } from "./contexts/WatchlistContext";
import { ContractTrackingProvider } from "./contexts/ContractTrackingContext";
import { ChartZoomProvider } from "./contexts/ChartZoomContext";
import { HistoricalModalProvider } from "./contexts/HistoricalModalContext";
import { ContractSummaryProvider } from "./contexts/ContractSummaryContext";
import { ExperienceModeProvider } from "./contexts/ExperienceModeContext";
import { TemplateGalleryProvider } from "./contexts/TemplateGalleryContext";
import { ConceptExperienceProvider } from "./concept/ConceptExperienceContext";

// Design System preview flag (theme/mmec-ds): off for members until stage-4 artboards exist.
applyDesignSystemPreview();
import { MMECThemeProvider } from "./contexts/MMECThemeContext";
import { TourProvider } from "./contexts/TourContext";
import { RegimeProvider } from "./contexts/RegimeContext";

const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement,
);

// ── Mobile chart embed ─────────────────────────────────────────────────────
// ?view=mobile-chart renders GEXChartV2 alone (no app shell, no AuthContext):
// the mobile app's WebView loads this with the JWT pre-seeded into
// localStorage['authToken'], which the global fetch wrapper already reads.
const embedView = new URLSearchParams(window.location.search).get("view");
const isMobileChartEmbed = embedView === "mobile-chart";
// ?view=live-gex renders the Real-Time GEX workspace alone (self-styled, needs
// no theme providers) — the clean capture/embed route for that view.
const isLiveGexEmbed = embedView === "live-gex";

if (isLiveGexEmbed) {
    const LiveGexEmbed =
        require("./LiveGexEmbed").default as React.ComponentType;
    root.render(<LiveGexEmbed />);
} else if (isMobileChartEmbed) {
    const MobileChartEmbed =
        require("./MobileChartEmbed").default as React.ComponentType;
    root.render(
        <MMECThemeProvider>
            <ThemeProvider>
                <SymbolMappingProvider>
                    <MobileChartEmbed />
                </SymbolMappingProvider>
            </ThemeProvider>
        </MMECThemeProvider>,
    );
} else {
root.render(
    <MMECThemeProvider>
        <ConceptExperienceProvider>
            <ExperienceModeProvider>
                <ThemeProvider>
                    <TemplateGalleryProvider>
                        <AuthProvider>
                            <LanguageProvider>
                                <RegimeProvider>
                                <DataSourceProvider>
                                    <DataModeProvider>
                                            <AdvancedAnalysisProvider>
                                                <SymbolMappingProvider>
                                                    <DateFilterProvider>
                                                        <TradingCallProvider>
                                                            <ContractTrackingProvider>
                                                            <WatchlistProvider>
                                                                <ChartZoomProvider>
                                                                    <HistoricalModalProvider>
                                                                        <ContractSummaryProvider>
                                                                            <TourProvider>
                                                                                <App />
                                                                            </TourProvider>
                                                                        </ContractSummaryProvider>
                                                                    </HistoricalModalProvider>
                                                                </ChartZoomProvider>
                                                            </WatchlistProvider>
                                                            </ContractTrackingProvider>
                                                        </TradingCallProvider>
                                                    </DateFilterProvider>
                                                </SymbolMappingProvider>
                                            </AdvancedAnalysisProvider>
                                    </DataModeProvider>
                                </DataSourceProvider>
                                </RegimeProvider>
                            </LanguageProvider>
                        </AuthProvider>
                    </TemplateGalleryProvider>
                </ThemeProvider>
            </ExperienceModeProvider>
        </ConceptExperienceProvider>
    </MMECThemeProvider>,
);
}
